import type {
  RuleBasedErrorEntry,
  VariationSummaryItem,
} from '@/lib/api/proofreading';
import type { ProofreadingIssueStatus } from '@/types/proofreading';
import {
  stampOriginsOnResults,
  stampOriginsOnVariationSummary,
  type RuleBasedErrorEntryStored,
  type VariationSummaryItemStored,
} from '@/lib/utils/proofreadingStoredTypes';

export type WorkProgressIssueStatus = 'applied' | 'rejected';

export type WorkProgressDataV1 = {
  version: 1;
  savedAt: string;
  hasExecutedProofreading: boolean;
  results: RuleBasedErrorEntry[];
  // eslint-disable-next-line camelcase -- API / persisted payload と同じキー
  variation_summary: { items: VariationSummaryItem[] };
  issueStatuses: Record<string, WorkProgressIssueStatus>;
};

export type WorkProgressDataV2 = {
  version: 2;
  savedAt: string;
  hasExecutedProofreading: boolean;
  results: RuleBasedErrorEntryStored[];
  // eslint-disable-next-line camelcase -- API / persisted payload と同じキー
  variation_summary: { items: VariationSummaryItemStored[] };
  issueStatuses: Record<string, WorkProgressIssueStatus>;
};

export type WorkProgressData = WorkProgressDataV1 | WorkProgressDataV2;

export type SerializeWorkProgressDataParams = {
  hasExecutedProofreading: boolean;
  results: RuleBasedErrorEntry[] | RuleBasedErrorEntryStored[];
  variationSummary: VariationSummaryItem[] | VariationSummaryItemStored[];
  issueStatuses: Map<string, ProofreadingIssueStatus>;
  savedAt?: string;
};

function isWorkProgressIssueStatus(value: unknown): value is WorkProgressIssueStatus {
  return value === 'applied' || value === 'rejected';
}

export function issueStatusesToRecord(
  issueStatuses: Map<string, ProofreadingIssueStatus>,
): Record<string, WorkProgressIssueStatus> {
  const record: Record<string, WorkProgressIssueStatus> = {};
  issueStatuses.forEach((status, issueId) => {
    if (status === 'applied' || status === 'rejected') {
      record[issueId] = status;
    }
  });
  return record;
}

export function recordToIssueStatusesMap(
  record: Record<string, WorkProgressIssueStatus>,
): Map<string, ProofreadingIssueStatus> {
  return new Map(Object.entries(record));
}

export function normalizeWorkProgressData(data: WorkProgressData): WorkProgressDataV2 {
  if (data.version === 2) {
    return {
      ...data,
      results: stampOriginsOnResults(data.results),
      variation_summary: {
        items: stampOriginsOnVariationSummary(data.variation_summary.items),
      },
    };
  }

  return {
    version: 2,
    savedAt: data.savedAt,
    hasExecutedProofreading: data.hasExecutedProofreading,
    results: stampOriginsOnResults(data.results),
    variation_summary: {
      items: stampOriginsOnVariationSummary(data.variation_summary.items),
    },
    issueStatuses: data.issueStatuses,
  };
}

export function serializeWorkProgressData(params: SerializeWorkProgressDataParams): string {
  const payload: WorkProgressDataV2 = {
    version: 2,
    savedAt: params.savedAt ?? new Date().toISOString(),
    hasExecutedProofreading: params.hasExecutedProofreading,
    results: stampOriginsOnResults(params.results),
    variation_summary: {
      items: stampOriginsOnVariationSummary(params.variationSummary),
    },
    issueStatuses: issueStatusesToRecord(params.issueStatuses),
  };
  return JSON.stringify(payload);
}

function isValidWorkProgressDataV1(value: unknown): value is WorkProgressDataV1 {
  if (value == null || typeof value !== 'object') return false;
  const data = value as Partial<WorkProgressDataV1>;
  if (data.version !== 1) return false;
  if (typeof data.savedAt !== 'string') return false;
  if (typeof data.hasExecutedProofreading !== 'boolean') return false;
  if (!Array.isArray(data.results)) return false;
  if (
    data.variation_summary == null
    || typeof data.variation_summary !== 'object'
    || !Array.isArray(data.variation_summary.items)
  ) {
    return false;
  }
  if (data.issueStatuses == null || typeof data.issueStatuses !== 'object') return false;

  const statuses = Object.values(data.issueStatuses);
  return statuses.every(isWorkProgressIssueStatus);
}

function isValidWorkProgressDataV2(value: unknown): value is WorkProgressDataV2 {
  if (value == null || typeof value !== 'object') return false;
  const data = value as Partial<WorkProgressDataV2>;
  if (data.version !== 2) return false;
  if (typeof data.savedAt !== 'string') return false;
  if (typeof data.hasExecutedProofreading !== 'boolean') return false;
  if (!Array.isArray(data.results)) return false;
  if (
    data.variation_summary == null
    || typeof data.variation_summary !== 'object'
    || !Array.isArray(data.variation_summary.items)
  ) {
    return false;
  }
  if (data.issueStatuses == null || typeof data.issueStatuses !== 'object') return false;

  const statuses = Object.values(data.issueStatuses);
  return statuses.every(isWorkProgressIssueStatus);
}

export function parseWorkProgressData(
  raw: string | null | undefined,
): WorkProgressDataV2 | null {
  if (raw == null || raw === '') return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isValidWorkProgressDataV2(parsed)) {
      return normalizeWorkProgressData(parsed);
    }
    if (isValidWorkProgressDataV1(parsed)) {
      return normalizeWorkProgressData(parsed);
    }
    return null;
  } catch {
    return null;
  }
}

/** 保存フラグが false でも results / variation があれば校正済みとみなす */
export function resolveHasExecutedProofreading(data: WorkProgressDataV2): boolean {
  if (data.hasExecutedProofreading) {
    return true;
  }
  if (data.results.length > 0) {
    return true;
  }
  return (data.variation_summary.items ?? []).some((item) =>
    item.variants.some((variant) => (variant.position ?? []).length > 0),
  );
}

/** 複数原稿に複製された progress から、校正結果を含むものを優先して選ぶ。 */
export function pickBestWorkProgressData(
  rawList: Array<string | null | undefined>,
): WorkProgressDataV2 | null {
  const parsed = rawList
    .map((raw) => parseWorkProgressData(raw))
    .filter((item): item is WorkProgressDataV2 => item != null);

  if (parsed.length === 0) {
    return null;
  }

  const withProofreading = parsed.filter(resolveHasExecutedProofreading);
  const pool = withProofreading.length > 0 ? withProofreading : parsed;

  return pool.reduce<WorkProgressDataV2 | null>((best, current) => {
    if (best == null || current.savedAt > best.savedAt) {
      return current;
    }
    return best;
  }, null);
}

/** @deprecated pickBestWorkProgressData を使用 */
export function pickLatestWorkProgressData(
  rawList: Array<string | null | undefined>,
): WorkProgressDataV2 | null {
  return pickBestWorkProgressData(rawList);
}
